import React from 'react'
import type { GroupResult } from '../../types'
import { getUIStrings } from '../../i18n/ui'
import { resolveColor } from '../../styles/colorContrast'
import { totalPoints, formatPoints } from '../../utils/score'

interface RadarVisualizationProps {
  groups: GroupResult[]
  overallPercentage: number
  lang?: string
}

const RadarVisualization: React.FC<RadarVisualizationProps> = ({ groups, overallPercentage, lang }) => {
  const t = getUIStrings(lang)
  const n = groups.length
  if (n === 0) return null

  const size = 400
  const cx = size / 2
  const cy = size / 2
  const maxR = 160
  const rings = [20, 40, 60, 80, 100]

  // Compute polygon points for a given radius at each axis
  const polygonPoints = (values: number[]) => {
    return values.map((val, i) => {
      const angle = (Math.PI * 2 * i) / n - Math.PI / 2
      const r = (val / 100) * maxR
      return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`
    }).join(' ')
  }

  // Label positions (slightly outside the max ring)
  const labelPos = (i: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2
    const r = maxR + 30
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) }
  }

  const dataValues = groups.map(g => g.percentage)

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Overall Score Card */}
      <div className="mb-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">{t.overallScore}</h3>
            <span className="text-4xl font-bold text-primary-600" data-testid="overall-points">
              {formatPoints(totalPoints(groups))}
            </span>
          </div>
          <div className={`text-xl font-bold ${
            overallPercentage >= 80 ? 'text-green-600' :
            overallPercentage >= 60 ? 'text-yellow-600' :
            overallPercentage >= 40 ? 'text-orange-600' :
            'text-red-600'
          }`}>
            {overallPercentage >= 80 ? t.excellent :
             overallPercentage >= 60 ? t.good :
             overallPercentage >= 40 ? t.fair :
             t.needsFocus}
          </div>
        </div>
      </div>

      {/* SVG Radar Chart */}
      <div className="flex justify-center mb-6">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
          {/* Background rings */}
          {rings.map((pct) => {
            const r = (pct / 100) * maxR
            const pts = Array.from({ length: n }, (_, i) => {
              const angle = (Math.PI * 2 * i) / n - Math.PI / 2
              return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`
            }).join(' ')
            return (
              <g key={`ring-${pct}`}>
                <polygon
                  points={pts}
                  fill="none"
                  stroke="#E5E7EB"
                  strokeWidth="1"
                />
                {/* Label on first axis */}
                <text
                  x={cx}
                  y={cy - r - 2}
                  textAnchor="middle"
                  className="fill-gray-400 text-xs"
                >
                  {pct}%
                </text>
              </g>
            )
          })}

          {/* Axis lines */}
          {groups.map((_, i) => {
            const angle = (Math.PI * 2 * i) / n - Math.PI / 2
            return (
              <line
                key={`axis-${i}`}
                x1={cx}
                y1={cy}
                x2={cx + maxR * Math.cos(angle)}
                y2={cy + maxR * Math.sin(angle)}
                stroke="#D1D5DB"
                strokeWidth="1"
              />
            )
          })}

          {/* Data polygon */}
          <polygon
            points={polygonPoints(dataValues)}
            fill="rgba(99, 102, 241, 0.2)"
            stroke="#6366F1"
            strokeWidth="2.5"
          />

          {/* Data points */}
          {dataValues.map((val, i) => {
            const angle = (Math.PI * 2 * i) / n - Math.PI / 2
            const r = (val / 100) * maxR
            const color = resolveColor(groups[i].color, '#6366F1')
            return (
              <circle
                key={`point-${i}`}
                cx={cx + r * Math.cos(angle)}
                cy={cy + r * Math.sin(angle)}
                r="5"
                fill={color}
                stroke="white"
                strokeWidth="2"
              />
            )
          })}

          {/* Axis labels */}
          {groups.map((group, i) => {
            const pos = labelPos(i)
            const color = resolveColor(group.color, '#374151')
            return (
              <g key={`label-${i}`}>
                <text
                  x={pos.x}
                  y={pos.y - 6}
                  textAnchor="middle"
                  className="font-semibold text-sm"
                  fill={color}
                >
                  {group.group_name}
                </text>
                <text
                  x={pos.x}
                  y={pos.y + 10}
                  textAnchor="middle"
                  className="font-bold text-sm"
                  fill={color}
                >
                  {Math.round(group.percentage)}%
                </text>
              </g>
            )
          })}
        </svg>
      </div>

      {/* Group Detail Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((group) => {
          const hex = resolveColor(group.color, '#6B7280')
          const score = Math.round(group.percentage)
          return (
            <div key={group.group_id} className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: hex }} />
                <h4 className="font-semibold text-gray-800">{group.group_name}</h4>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold" style={{ color: hex }}>{score}%</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  score >= 80 ? 'bg-green-100 text-green-700' :
                  score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {score >= 80 ? t.strong : score >= 60 ? t.moderate : t.needsAttention}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${score}%`, backgroundColor: hex }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RadarVisualization
